import { ChevronUpIcon } from "@heroicons/react/24/solid";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CPETInformation } from "../../api/forms/cpet-info/types";
import { DynamicLoops } from "../../api/forms/fv-loops/types";
import {
    AdvancedMedicalInfo,
    BasicMedicalInfo,
} from "../../api/forms/medical-info/types";
import {
    AdvancedPersonalData,
    BasicPersonalData,
} from "../../api/forms/personal-data/types";
import {
    PostSpirometryData,
    PreSpirometryData,
} from "../../api/forms/spirometry/types";
import { Symptoms } from "../../api/forms/symptoms/types";
import BottomNavigationButtons from "../../components/BottomNavigationButtons";
import AdvancedMedicalInfoForm from "../../components/data-input-forms/AdvancedMedicalInfoForm";
import AdvancedPersonalDataForm from "../../components/data-input-forms/AdvancedPersonalDataForm";
import BasicMedicalInfoForm from "../../components/data-input-forms/BasicMedicalInfoForm";
import BasicPersonalDataForm from "../../components/data-input-forms/BasicPersonalDataForm";
import CPETInfoForm from "../../components/data-input-forms/CPETInfoForm";
import DynamicLoopsForm from "../../components/data-input-forms/DynamicLoopsForm";
import SymptomsForm from "../../components/data-input-forms/SymptomsForm";
import SpirometryForms from "../../components/SpirometryForms";
import useFormRef from "../../hooks/useFormRef";
import useTestData from "../../hooks/useTestData";
import useTestId from "../../hooks/useTestId";
import { ROUTES } from "../config";
import useConfirm from "../../hooks/useConfirm";
import usePrompt from "../../hooks/usePrompt";
import { cancelDataInput, confirmDataInput } from "../../api/data-input";
import BloodTestForm from "../../components/data-input-forms/BloodTestForm";
import { BloodTestRestAndExerciseData } from "../../api/forms/blood-test/types";
import useAlert from "../../hooks/useAlert";
import { validatePreSpirometryField } from "../../api/forms/spirometry";
import { clearGraphsCache } from "../../hooks/useGraphsFactory";

export default function DataInput() {
    const { mutate: mutateTestData } = useTestData();
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [isDirty, setIsDirty] = useState(true);
    const [shouldBlock, setShouldBlock] = useState(true);
    const [errors, setErrors] = useState<Record<string, boolean>>({});
    const [isConfirmLoading, setIsConfirmLoading] = useState(false);

    const hasErrors = Object.values(errors).some((hasError) => hasError);

    const testId = useTestId();
    const navigate = useNavigate();
    const { value: testData } = useTestData();
    const { show } = useConfirm();
    const { show: showAlert } = useAlert();

    const showValidationAlert = useCallback(
        (
            message: string = "Please fix the errors before submitting the form"
        ) => {
            showAlert({
                title: "Validation Error",
                description: message,
            });
        },
        [showAlert]
    );

    // forms refs
    const symptomsRef = useFormRef<Symptoms>();
    const basicPersonalDataRef = useFormRef<BasicPersonalData>();
    const advancedPersonalDataRef = useFormRef<AdvancedPersonalData>();
    const dynamicLoopsRef = useFormRef<DynamicLoops>();
    const basicMedicalInfoRef = useFormRef<BasicMedicalInfo>();
    const advancedMedicalInfoRef = useFormRef<AdvancedMedicalInfo>();
    const cpetInfoRef = useFormRef<CPETInformation>();
    const preSpirometryRef = useFormRef<PreSpirometryData>();
    const postSpirometryRef = useFormRef<PostSpirometryData>();
    const bloodTestRef = useFormRef<BloodTestRestAndExerciseData>();

    const refs = useMemo(
        () => [
            basicPersonalDataRef,
            symptomsRef,
            basicMedicalInfoRef,
            preSpirometryRef,
            postSpirometryRef,
            dynamicLoopsRef,
            advancedPersonalDataRef,
            advancedMedicalInfoRef,
            cpetInfoRef,
            bloodTestRef,
        ],
        [
            basicPersonalDataRef,
            symptomsRef,
            basicMedicalInfoRef,
            preSpirometryRef,
            postSpirometryRef,
            dynamicLoopsRef,
            advancedPersonalDataRef,
            advancedMedicalInfoRef,
            cpetInfoRef,
            bloodTestRef,
        ]
    );

    useEffect(() => {
        const checkIfDirty = () => {
            const isFormDirty = refs.some((ref) => ref.current?.isDirty);
            setIsDirty(isFormDirty);
        };
        checkIfDirty();
        const interval = setInterval(checkIfDirty, 1000);

        return () => clearInterval(interval);
    }, [refs]);

    const handleFormsSubmit = useCallback(
        async (isFormDirty: boolean, onSuccessGoToInterpretation?: boolean) => {
            try {
                if (!testData) {
                    showValidationAlert(
                        "Test data is not available. Please try again later."
                    );
                    return;
                }

                if (hasErrors) {
                    showValidationAlert();
                    return;
                }

                let successfulSubmit = true;
                let newTestId = testId;

                const handleFormSubmit = (ref: any) => {
                    return ref
                        .onSubmit()
                        .then(
                            (resData: {
                                success: boolean;
                                errorMessage: string | null;
                                testId?: string;
                            }) => {
                                if (resData.testId) {
                                    newTestId = resData.testId;
                                }
                                if (!resData.success) {
                                    successfulSubmit = false;
                                }
                                return resData.success;
                            }
                        );
                };

                // sequentially submit all forms
                setIsConfirmLoading(true);
                // to ensure pre-spirometry is submitted last
                const sortedRefs = refs.sort((a, b) => {
                    if (a === preSpirometryRef) return 1;
                    if (b === preSpirometryRef) return -1;
                    return 0;
                });
                for (const ref of sortedRefs) {
                    await handleFormSubmit(ref.current);
                }

                // TODO: for now submitting the forms in parallel - uncomment the following lines
                // const refsWithoutCPET = storedRefs.filter(
                //     (ref) => ref !== cpetInfoRef
                // );
                // const promises = refsWithoutCPET.map(ref => handleFormSubmit(ref.current));

                // await Promise.all(promises);
                // await handleFormSubmit(cpetInfoRef.current); // CPET form is submitted last

                setIsDirty(false);
                setShouldBlock(false);
                // remove graphs cache
                clearGraphsCache();

                if (successfulSubmit) {
                    if (
                        newTestId &&
                        (isFormDirty || testData?.navbarStatus === 0)
                    ) {
                        await confirmDataInput(newTestId);
                    }
                    const updatedTestData = await mutateTestData();

                    if (onSuccessGoToInterpretation) {
                        const navigateTo =
                            updatedTestData?.testStatus === "Not completed"
                                ? `/test/${newTestId}/${ROUTES.INTERPRETATION.path}/${ROUTES.INTERPRETATION.subroutes.ANAEROBIC_THRESHOLD.path}`
                                : `/test/${newTestId}/${ROUTES.INTERPRETATION.path}`;
                        navigate(navigateTo);
                    }
                }
            } catch (error) {
                console.error("Error submitting forms:", error);
            } finally {
                setIsConfirmLoading(false);
            }
        },
        [navigate, refs, testData, testId, hasErrors]
    );

    const cancelDataInputHandler = async () => {
        await cancelDataInput(String(testId));
    };

    usePrompt({
        isDirty: isDirty && shouldBlock,
        title: "You have unsaved changes.",
        confirmText: "Save and Confirm",
        cancelText: "Do not Save",
        disableNavigateOnConfirm: hasErrors,
        onConfirm: () => {
            if (hasErrors) {
                showValidationAlert();
                return;
            }
            return handleFormsSubmit(isDirty, false);
        },
        onCancel: cancelDataInputHandler,
    });

    const onConfirmClick = () => {
        if (hasErrors) {
            showValidationAlert();
            return;
        }

        const isFormDirty = refs.some((ref) => ref.current?.isDirty);
        if (isFormDirty) {
            show({
                onConfirm: () => handleFormsSubmit(isFormDirty, true),
                onCancel: () => {},
            });
        } else {
            handleFormsSubmit(isFormDirty, true);
        }
    };

    const triggerPrespirometryUpdate = async () => {
        const fvcValue = preSpirometryRef.current?.getFormData()?.fvc;
        if (!fvcValue) return;

        await validatePreSpirometryField({
            testId: testId!,
            fieldId: "fvc",
            value: fvcValue.measured,
        });
        await preSpirometryRef.current?.onSubmit();
    };

    return (
        <div className="h-full flex flex-col gap-10 pb-bottomPanel">
            <div className="h-full flex flex-col max-w-[1558px] mx-auto pb-5">
                <div className="flex gap-4">
                    <div className="flex gap-2.5">
                        <BasicPersonalDataForm
                            ref={basicPersonalDataRef}
                            setErrors={setErrors}
                            triggerPrespirometryUpdate={
                                triggerPrespirometryUpdate
                            }
                        />
                        <div className="flex flex-col gap-2.5">
                            <BasicMedicalInfoForm ref={basicMedicalInfoRef} />
                            <SymptomsForm
                                ref={symptomsRef}
                                withECG
                                withSymptoms
                            />
                        </div>
                    </div>
                    <div className="w-[1px] mx-1 shrink-0 bg-gray-disabled" />
                    <div className="flex flex-col gap-2.5">
                        <SpirometryForms
                            preSpirometryRef={preSpirometryRef}
                            postSpirometryRef={postSpirometryRef}
                            setErrors={setErrors}
                        />
                        <DynamicLoopsForm
                            ref={dynamicLoopsRef}
                            setErrors={setErrors}
                        />
                    </div>
                </div>
                <div
                    className={`${
                        showAdvancedOptions ? "" : "hidden"
                    } w-full h-full flex gap-2.5 mt-12 xl:mt-20`}
                >
                    <AdvancedPersonalDataForm
                        ref={advancedPersonalDataRef}
                        triggerPrespirometryUpdate={triggerPrespirometryUpdate}
                    />
                    <div className="flex flex-col gap-10">
                        <AdvancedMedicalInfoForm ref={advancedMedicalInfoRef} />
                        <CPETInfoForm ref={cpetInfoRef} />
                    </div>
                    <div className="ms-[31px]">
                        <BloodTestForm ref={bloodTestRef} />
                    </div>
                </div>

                <button
                    className="text-blue-medium underline font-bold flex gap-2 items-center mt-6 text-sm xl:text-base"
                    onClick={() => {
                        setShowAdvancedOptions((prev) => !prev);
                        setTimeout(() => {
                            window.scrollBy({
                                top: 600,
                                behavior: "smooth",
                            });
                        }, 100);
                    }}
                >
                    {showAdvancedOptions ? "Hide" : "Show"} Advanced Options
                    <ChevronUpIcon
                        className={`${
                            showAdvancedOptions ? "rotate-0" : "rotate-180"
                        } transition-all w-4 h-4`}
                    />
                </button>
            </div>
            <BottomNavigationButtons
                isFixed
                cancelText="Back"
                cancelLinkPath={`/${ROUTES.OPEN_TEST.path}`}
                onConfirmClick={onConfirmClick}
                isConfirmLoading={isConfirmLoading}
            />
        </div>
    );
}
